import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addHours, startOfToday } from 'date-fns';
import { normalize, schema } from 'normalizr';
import {
    ApplicationRestrictionDto,
    AppSettingsDto,
    DayOfWeek,
    OrganizationDto,
    PricelistGroupingType,
    PricelistMailingScheduleDto,
    SavePricelistMailingScheduleDto,
    SendPricelistsToCustomersRequest,
    TableSettingsDto,
    UpdateApplicationSettingsDto,
    UserSettingsDto
} from '../../../common/PricePrgClient';
import { createAsyncAction } from '../../../utils';

const namespaces = {
    appSettings: 'appSettings',
    organization: 'organization',
    pricelistMailing: 'pricelistMailing',
    settingsUi: 'settingsUi',
    userSettings: 'userSettings',
    applicationRestrictions: 'applicationRestrictions',
};

export const fetchUserSettings = createAsyncAction<void, UserSettingsDto>(
    namespaces.userSettings,
    'fetchUserSettings'
);

export const updateTableSettings = createAsyncAction<TableSettingsDto, void>(
    namespaces.userSettings,
    'updateTableSettings'
);

export const fetchOrganization = createAsyncAction<void, OrganizationDto>(
    namespaces.organization,
    'fetchOrganization'
);
export const updateOrganization = createAsyncAction<OrganizationDto, OrganizationDto, void>(
    namespaces.organization,
    'updateOrganization'
);
const organizationSlice = createSlice({
    name: namespaces.organization,
    initialState: {} as OrganizationDto,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchOrganization.success, (state, action) => action.payload)
            .addCase(updateOrganization.success, (state, action) => action.payload)
});

export const fetchPricelistMailingSchedule = createAsyncAction<void, PricelistMailingScheduleDto>(
    namespaces.pricelistMailing,
    'fetchPricelistMailingSchedule'
);

export const updatePricelistMailingSchedule = createAsyncAction<
    SavePricelistMailingScheduleDto,
    SavePricelistMailingScheduleDto
>(namespaces.pricelistMailing, 'updatePricelistMailingScheduleRequest');

export const sendPricelistsToEmail = createAsyncAction<
    {
        mailSendoutId: string;
        email: string;
        customerIds: number[];
        validAt: string;
    },
    void,
    void
>(namespaces.pricelistMailing, 'sendPricelistsToEmail');

export const sendPricelistsToCustomers = createAsyncAction<
    {
        mailSendoutId: string;
        payload: SendPricelistsToCustomersRequest;
    },
    void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
>(namespaces.pricelistMailing, 'sendPricelistsToCustomers');

const pricelistMailingSlice = createSlice({
    name: namespaces.pricelistMailing,
    initialState: {
        timeOfDay: addHours(startOfToday(), 5).toISOString(),
        weekdays: [
            DayOfWeek.Monday,
            DayOfWeek.Tuesday,
            DayOfWeek.Wednesday,
            DayOfWeek.Thursday,
            DayOfWeek.Friday
        ],
        customerIds: [],
        enabled: false,
        mailAttachmentTypes: [],
        pricelistGroupingType: PricelistGroupingType.ProductGroup
    } as PricelistMailingScheduleDto,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchPricelistMailingSchedule.success, (state, action) => action.payload)
            .addCase(updatePricelistMailingSchedule.success, (state, action) => ({
                ...state,
                ...action.payload
            }))
});

export const fetchAppSettings = createAsyncAction<void, AppSettingsDto>(
    namespaces.appSettings,
    'fetchAppSettings'
);

export const saveGeneralAppSettings = createAsyncAction<UpdateApplicationSettingsDto, UpdateApplicationSettingsDto>(
    namespaces.appSettings,
    'saveGeneralAppSettings'
);

const appSettingsSlice = createSlice({
    name: namespaces.appSettings,
    initialState: {
        pricelistMailingWeekdays: [],
        applicationVersion: '',
        mailSendouts: [],
        availableCurrencies: [],
        availableLanguages: [],
    } as AppSettingsDto,
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchAppSettings.success, (state, action) => action.payload)
            .addCase(updatePricelistMailingSchedule.success, (state, action) => {
                state.pricelistMailingWeekdays = action.payload.weekdays;
            })
            .addCase(saveGeneralAppSettings.success, (state, action) => ({ ...state, ...action.payload }))
});

type SettingsUi = {
    isSavingOrganization: boolean;
    isSendingPricelistMails: boolean;
    isSendPricelistsToMailDialogOpen: boolean;
    isSendPricelistsToCustomersDialogOpen: boolean;
};

const uiSlice = createSlice({
    name: namespaces.settingsUi,
    initialState: {
        isSavingOrganization: false,
        isSendingPricelistMails: false,
        isSendPricelistsToMailDialogOpen: false,
        isSendPricelistsToCustomersDialogOpen: false
    } as SettingsUi,
    reducers: {
        showSendPricelistMailsToUserDialog: (state, action: PayloadAction<boolean>) => {
            state.isSendPricelistsToMailDialogOpen = action.payload;
        },
        showSendPricelistsToCustomersDialog: (state, action: PayloadAction<boolean>) => {
            state.isSendPricelistsToCustomersDialogOpen = action.payload;
        }
    },
    extraReducers: builder =>
        builder
            .addCase(updateOrganization.request, state => {
                state.isSavingOrganization = true;
            })
            .addCase(updateOrganization.success, state => {
                state.isSavingOrganization = false;
            })
            .addCase(updateOrganization.failure, state => {
                state.isSavingOrganization = false;
            })
            .addCase(sendPricelistsToEmail.request, state => {
                state.isSendingPricelistMails = true;
            })
            .addCase(sendPricelistsToEmail.failure, state => {
                state.isSendingPricelistMails = false;
            })
            .addCase(sendPricelistsToEmail.success, state => {
                state.isSendingPricelistMails = false;
                state.isSendPricelistsToMailDialogOpen = false;
            })
            .addCase(sendPricelistsToCustomers.request, state => {
                state.isSendingPricelistMails = true;
            })
            .addCase(sendPricelistsToCustomers.failure, state => {
                state.isSendingPricelistMails = false;
            })
            .addCase(sendPricelistsToCustomers.success, state => {
                state.isSendingPricelistMails = false;
                state.isSendPricelistsToCustomersDialogOpen = false;
            })
});
export const { showSendPricelistMailsToUserDialog, showSendPricelistsToCustomersDialog } = uiSlice.actions;

const applicationRestrictionEntity = new schema.Entity<ApplicationRestrictionDto>(
    namespaces.applicationRestrictions,
    undefined,
    {
        idAttribute: 'entityType'
    }
);

export const getApplicationRestrictions = createAsyncAction<void, ApplicationRestrictionDto[]>(
    namespaces.applicationRestrictions,
    'getApplicationRestrictions'
);

export const updateApplicationRestriction = createAsyncAction<ApplicationRestrictionDto, ApplicationRestrictionDto>(
    namespaces.applicationRestrictions,
    'updateApplicationRestrictions'
);

const applicationRestrictionsSlice = createSlice({
    name: namespaces.applicationRestrictions,
    initialState: {} as { [key: string]: ApplicationRestrictionDto },
    reducers: {},
    extraReducers: builder =>
        builder.addCase(getApplicationRestrictions.success, (_, action) => {
            const normalized = normalize(action.payload, [applicationRestrictionEntity]);
            return normalized.entities.applicationRestrictions ?? {};
        }).addCase(updateApplicationRestriction.success, (state, action) => {
            state[action.payload.entityType] = action.payload;
        })
});

export default combineReducers({
    app: appSettingsSlice.reducer,
    organization: organizationSlice.reducer,
    ui: uiSlice.reducer,
    pricelistMailing: pricelistMailingSlice.reducer,
    applicationRestrictions: applicationRestrictionsSlice.reducer
});
